/**
 * useXdcWallet Hook
 *
 * Custom React hook for managing XDC Network wallet data and state
 */

import { useQuery } from '@tanstack/react-query';
import { xdcAPI, weiToXdc } from '../services/xdcApi';
import type { XDCWalletData } from '../types/xdc.types';

/**
 * Fetch XDC price
 */
export const useXdcPrice = () => {
  return useQuery({
    queryKey: ['xdc', 'price'],
    queryFn: () => xdcAPI.getXdcPrice(),
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
};

/**
 * Fetch wallet data for multiple addresses
 */
export const useXdcWallets = (addresses: string[]) => {
  const { data: xdcPrice = 0 } = useXdcPrice();

  const {
    data: wallets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['xdc', 'wallets', addresses, xdcPrice],
    queryFn: () => xdcAPI.getMultipleWalletsData(addresses, xdcPrice),
    enabled: addresses.length > 0 && xdcPrice > 0,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Calculate aggregated metrics
  const totalBalance = wallets?.reduce((sum, w) => sum + w.balance, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.balanceUsd, 0) || 0;
  const totalStaked = wallets?.reduce((sum, w) => sum + w.stakedAmount, 0) || 0;
  const totalStakedUsd = wallets?.reduce((sum, w) => sum + w.stakedAmountUsd, 0) || 0;
  const totalPendingRewards = wallets?.reduce((sum, w) => sum + w.pendingRewards, 0) || 0;
  const totalPendingRewardsUsd = wallets?.reduce((sum, w) => sum + w.pendingRewardsUsd, 0) || 0;

  // Count unique tokens
  const uniqueTokens = new Set<string>();
  wallets?.forEach(w => w.tokens.forEach(t => uniqueTokens.add(t.address)));

  // Count masternodes
  const masternodeCount = wallets?.filter(w => w.isMasternode).length || 0;

  return {
    wallets: wallets || [],
    isLoading,
    error,
    refetch,
    xdcPrice,
    // Aggregated metrics
    totalBalance,
    totalBalanceUsd,
    totalStaked,
    totalStakedUsd,
    totalPendingRewards,
    totalPendingRewardsUsd,
    totalTokens: uniqueTokens.size,
    masternodeCount,
  };
};
